import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { Form, FormikProvider, useFormik } from 'formik';
import {
  Box,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ModalComponent from '../../../../../components/modal-layout/ModalComponent';
import style from '../../../settings/components/ProjectSetting.module.css';
import ProjectMemberSchema, { UpdateProjectMemberSchema } from '../../../schema/projectMemberSchema';
import { useProject } from '../../../Layout/header/context';
import Iconify from '../../../../../components/iconify';
import { GUEST, ROLE_LEVEL } from '../../../../../Constants/ProjectConstants';
import { isAdmin } from '../../../../../utils/share';
import { getMemberRole } from '../../../../../services/projectService';
import FilterProjectRoles from '../../../../../sections/@dashboard/projectDetails/FilterProjectRoles';
import { formatHours } from '../../../../../utils/timeCalculation';
import { TEAMMEMBER } from '../../../../../Constants/ProjectRoleConstants';

const ProjectMemberFormModal = ({ open, handleClose, initialValues, selected, onSubmit }) => {
  const { id } = useParams();
  const { projectDetails } = useProject();
  const [projectRoles, setProjectRoles] = useState([]);
  const [userRole, setUserRole] = useState({});
  const [loading, setLoading] = useState(false);
  const [isActive, setIsActive] = useState(selected?.isActive);
  const [showDatePickers, setShowDatePickers] = useState(false);
  let totalAllotedHours = 0;

  if (projectDetails?.projectmember?.length > 0) {
    totalAllotedHours = projectDetails?.projectmember?.reduce((sum, member) => sum + member?.totalTime / 3600, 0);
  }

  const projectRoleList =
    selected?.projectRoles?.roleName === GUEST
      ? projectRoles?.filter((role) => role.roleName === GUEST)
      : projectRoles?.filter(
          (role) =>
            role.roleName !== GUEST && (!(ROLE_LEVEL[userRole?.roleName] > ROLE_LEVEL[role?.roleName]) || isAdmin())
        );

  const formik = useFormik({
    initialValues,
    validationSchema: UpdateProjectMemberSchema(projectDetails, totalAllotedHours, selected),
    onSubmit,
  });

  const { touched, handleSubmit, errors, isSubmitting, getFieldProps, setFieldValue } = formik;

  useEffect(
    () => () => {
      formik.resetForm();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open]
  );

  useEffect(() => {
    const fetchProjectRoles = async (id, role) => {
      try {
        const roles = await FilterProjectRoles(id, role);
        setProjectRoles(roles);
      } catch (e) {
        toast.error(e.message);
      }
    };

    const projectMemberRole = async () => {
      setLoading(true);
      try {
        const roles = await getMemberRole(id);
        setUserRole(roles.data.data);
        await fetchProjectRoles(id, roles.data.data);
      } catch (e) {
        toast.error(e.message);
      } finally {
        setLoading(false);
      }
    };

    projectMemberRole();
  }, [id]);

  const handleIsActive = (event) => {
    const newIsActive = event.target.checked;
    setIsActive(newIsActive);
    setFieldValue('isActive', newIsActive);

    setShowDatePickers(newIsActive !== selected?.isActive);
    if (newIsActive !== selected?.isActive) {
      const date = selected?.endDate ? selected?.endDate : new Date();
      setFieldValue('startDate', new Date(date));
    }
  };
  return (
    <ModalComponent openModal={open} handleClose={handleClose}>
      <Box className={`${style.container} ${style.inputBoxStyle}`}>
        <Typography variant="h5" align="center" className={style.memberUpdateModelTitle}>
          Edit Assigned project hours
        </Typography>
        <Box className={`${style.Container} ${style.inputDiv}`}>
          <IconButton color="primary" onClick={handleClose}>
            <Iconify icon="ic:round-clear" />
          </IconButton>
        </Box>
      </Box>
      <Divider mb={2} />
      <Container maxWidth="sm" sx={{ mt: 2 }}>
        <FormikProvider value={formik}>
          <Form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>User</InputLabel>
                <Select value={selected?.user?.id} disabled label="User">
                  <MenuItem value={selected?.user?.id}>{selected?.user?.userName}</MenuItem>
                </Select>
                {touched.userId && errors.userId && (
                  <FormHelperText className={style.helperText}>{errors.userId}</FormHelperText>
                )}
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  {...getFieldProps('projectRoleId')}
                  error={touched.projectId && Boolean(errors.projectId)}
                  helperText={touched.projectId && errors.projectId}
                  label={loading ? 'Loading...' : 'Role'}
                  placeholder={loading ? 'Loading...' : ''}
                  onChange={(event, value) => {
                    setFieldValue('projectRoleId', value?.props?.value);
                  }}
                  disabled={selected?.projectRoles?.roleName === GUEST}
                >
                  {projectRoleList.map((role, i) => (
                    <MenuItem value={role.id} key={i}>
                      {role.roleName.toLowerCase() === TEAMMEMBER ? 'Team Member' : role.roleName}
                    </MenuItem>
                  ))}
                </Select>
                {touched.projectId && errors.projectId && (
                  <FormHelperText className={style.helperText}>{errors.projectId}</FormHelperText>
                )}
              </FormControl>
              <Grid container justifyContent={'space-between'}>
                <Grid item>
                  <TextField
                    label="Project Available Hours"
                    value={formatHours((projectDetails?.totalHrs - totalAllotedHours).toFixed(2))}
                    disabled
                  />
                </Grid>
                <Grid item>
                  <TextField
                    label="Project Consumed Hours"
                    value={formatHours(totalAllotedHours.toFixed(2)) ?? 0}
                    disabled
                  />
                </Grid>
              </Grid>

              <Grid container direction={'row'} gap={9}>
                <Grid item>
                  <TextField
                    label="Add Hours"
                    type="number"
                    {...getFieldProps('userHours')}
                    error={touched.userHours && Boolean(errors.userHours)}
                    helperText={touched.userHours && errors.userHours}
                    fullWidth
                  />
                </Grid>
                <Grid item>
                  <FormControlLabel
                    control={
                      <Switch
                        {...getFieldProps('isActive')}
                        color="primary"
                        checked={isActive}
                        onChange={(e) => {
                          handleIsActive(e);
                        }}
                      />
                    }
                    label="Is Active"
                  />
                </Grid>
              </Grid>
              {showDatePickers && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Grid container direction={'column'} justifyContent={'space-between'}>
                    <Grid item >
                      <Box mb={2} >
                        <DatePicker
                          label="Start Date"
                          value={formik.values.startDate}
                          onChange={(newValue) => setFieldValue('startDate', newValue)}
                          inputFormat="DD-MMM-YYYY"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              {...getFieldProps('startDate')}
                              error={touched.startDate && Boolean(errors.startDate)}
                              helperText={touched.startDate && errors.startDate}
                              fullWidth
                              style={{ width: '100%' }}
                            />
                          )}
                        />
                      </Box>
                    </Grid>
                    {/* <Grid item>
                      <Box mb={2}>
                        <DatePicker
                          label="End Date"
                          value={formik.values.endDate}
                          onChange={(newValue) => setFieldValue('endDate', newValue)}
                          inputFormat="DD-MMM-YYYY"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              {...getFieldProps('endDate')}
                              error={touched.endDate && Boolean(errors.endDate)}
                              helperText={touched.endDate && errors.endDate}
                              fullWidth
                              style={{ maxWidth: '225px' }}
                            />
                          )}
                        />
                      </Box>
                    </Grid> */}
                  </Grid>
                </LocalizationProvider>
              )}
              <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
                Save
              </LoadingButton>
            </Stack>
          </Form>
        </FormikProvider>
      </Container>
    </ModalComponent>
  );
};

ProjectMemberFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  selected: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default ProjectMemberFormModal;
